import { Download, Maximize2, X } from "lucide-react";
import { useEffect } from "react";

type TImageModalProps = {
  imageUrl: string;
  imageName?: string;
  onClose: () => void;
};

export default function ImageModal({ imageUrl, imageName, onClose }: TImageModalProps) {
  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = imageName ?? "image";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(imageUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex h-full max-h-full w-full max-w-7xl flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Action buttons */}
        <div className="absolute left-4 top-4 z-10 flex gap-2">
          <button
            onClick={handleDownload}
            className="rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
            aria-label="Download"
            title="Download image"
          >
            <Download className="h-5 w-5" />
          </button>
          <button
            onClick={handleOpenInNewTab}
            className="rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
            aria-label="Open in new tab"
            title="Open in new tab"
          >
            <Maximize2 className="h-5 w-5" />
          </button>
        </div>

        {/* Image container */}
        <div className="flex flex-1 items-center justify-center overflow-hidden">
          <img
            src={imageUrl}
            alt={imageName ?? "Preview"}
            className="max-h-full max-w-full object-contain"
            onClick={onClose}
          />
        </div>

        {/* Image name */}
        {imageName && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 transform rounded-lg bg-black/70 px-4 py-2 text-white">
            <p className="max-w-md truncate text-sm font-medium">{imageName}</p>
          </div>
        )}
      </div>
    </div>
  );
}
